<template>
  <div>
    <!-- Affichage d'une barre de progression pendant le chargement des données -->
    <load-progress :condition="!dataLoaded" label="Chargement des données"></load-progress>
    <!-- Affichage du tree view et des éditeurs selon l'objet sélectionné -->

    <div v-if="dataLoaded">
        <fiche-container ref="FICHE_CONTAINER"
                         :fiche="selectedFiche"
                         :route="notifRoute"
                         :attribut="attribut"
                         :route-element="routeElement"
                         @back="back"
                         @selectItem="selectItem">
        </fiche-container>
    </div>
    <div v-for="(button, index) in extraButtons" :key="index">
      <loader-button class="ms-2"
                     :outlined="button.outlined"
                     @callback="button.callback()"
                     :label="button.label"
                     :disabled="button.disabled">
      </loader-button>
    </div>
    <section-container :dialogOpen="showWizard"
                       :section="sectionWizard"
                       @closeDialog="hideWizard">
    </section-container>
  </div>
</template>

<script lang="ts">
  import { defineComponent, markRaw, type PropType } from "vue";
  import LoadProgress from '@/framework/base/components/LoadProgress.vue';
  import {
    storeSession,
    storeClient,
    storeContact,
    storeNotif,
    storeUser
  } from '@/generated/stores';
  import { CiRouter } from '@/framework/base/helpers/NavHelper';
  import Route from '@/generated/classes/Route';
  import ModuleBase from '@/framework/base/components/ModuleBase';
  import FichePreferenceExtend from '@/extend/fiches/FichePreferenceExtend.vue';
  import SectionModeConnexion from '@/custom/sections/SectionModeConnexion.vue';
  import { Fiches, Modules, Sections } from '@/generated/classes/AppSchema';
  import { GenModule, RessourceGenModule } from '@/generated/ressources/generatedRessources';
  import Wizard from '@/extend/classes/Wizard';
  import Section from '@/framework/base/classes/Section';
  import SectionWizard from '@/framework/wizard/components/SectionWizard.vue';
  import Fiche from '@/framework/base/classes/Fiche';
  import { RouteElements } from '@/framework/base/classes/Section';
  import User from '@/generated/classes/User';
  import FicheContainerAbstract from "@/framework/base/components/FicheContainerAbstract.vue";

  export function ModulePreferenceOpenFiche(route: Route | undefined): RouteElements {
    const section = new Section();
    const fiche = new Fiche();
    if (route && route.fiche) {
      switch (route.fiche) {
        case Fiches.PREFERENCE: {
          fiche.component = FichePreferenceExtend;
          fiche.type = 'contact'
          if (route && route.ficheObjId != null) {
            if (route.ficheObjId == 0) {
              fiche.data = storeContact.createNewContact();
            } else {
              fiche.data = storeContact.getContact(route.ficheObjId);
            }
          }
          break;
        }
        case Fiches.CONTAINER: {
          fiche.component = FicheContainerAbstract;
          fiche.type = 'contact'
          if (route && route.ficheObjId != null) {
            if (route.ficheObjId == 0) {
              fiche.data = storeContact.createNewContact();
            } else {
              fiche.data = storeContact.getContact(route.ficheObjId);
            }
          }
          break;
        }
      }
    } else {
      fiche.component = FichePreferenceExtend;
      fiche.type = 'contact'
      if (route && route.ficheObjId != null) {
        if (route.ficheObjId == 0) {
          fiche.data = storeContact.createNewContact();
        } else {
          fiche.data = storeContact.getContact(route.ficheObjId);
        }
      }
    }
    if (route && route.section && route.section != Sections.NONE) {
      switch (route.section) {
        case Sections.MODE_CONNEXION: {
          section.component = SectionModeConnexion;
          section.type = 'user'
          if (route.sectionObjId == 0) {
            section.data = storeUser.createNewUser();
          } else {
            section.data = storeUser.getUser(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
      }
    }
    if (fiche && fiche.component) {
      fiche.component = markRaw(fiche.component);
    }
    if (section && section.component) {
      section.component = markRaw(section.component);
    }
    const res = new RouteElements();
    res.fiche = fiche;
    res.section = section;
    return res;
  }

  export default defineComponent({
    extends: ModuleBase,
    components: {
      LoadProgress,
    },

    props: {
    },

    created (): void {
      if (this.route) {
        this.notifRoute = this.route;
      }
      const session = storeSession.connectedUser;
      if (session) {
        const user = storeUser.getUser(session.id);
        if (user.modules.includes(Modules.PREFERENCE)) {
          this.wizardDone = true;
        }
      }
      if (!this.wizardDone) {
        Wizard.showWizard(Modules.PREFERENCE).then((res) => {
          if (res.id > 0) {
            this.wizard = res;
            this.showWizard = true;
          } else {
            User.acceptCgu(true);
            this.wizardDone = true;
          }
        })
      }
      this.loadStores();
    },

    data() {
      const attribut: unknown = undefined;
      const route = CiRouter.getRoute();
      return {
        wizard: new Wizard(),
        attribut,
        notifRoute: new Route(),
        route,
      }
    },

    computed: {
      ressource () {
        return RessourceGenModule(GenModule.MODULE_PREFERENCE);
      },
      sectionWizard (): Section {
        const section = new Section();
        section.component = SectionWizard;
        section.data = this.wizard;
        section.helper = true;
        return section;
      },

      storesLoaded (): boolean {
        return true;
      },

      dataLoaded (): boolean {
        const res = this.wizardDone && this.storesLoaded
        if (res && this.init) {
          this.setInit(false);
          if (!this.route) {
            this.selectItem(this.route);
          }
          CiRouter.setRouteChangedSelector(this.routeChanged);
        }
        return res;
      }
    },

    methods: {
      loadStores() : void{
        // Load and register events on used server classes
        storeClient.registerClient();
        storeContact.registerContact();
        storeNotif.registerNotif();
        storeUser.registerUser();
      },
      setInit(value: boolean) : void{
        this.init = value;
      },
      routeChanged (route: Route): void {
        this.selectItem(route);
      },
      selectItem(route: Route | undefined): void {
        const routeElement = ModulePreferenceOpenFiche(route);
        this.routeElement = routeElement;
        this.selectedFiche = routeElement.fiche;
        this.selectedFiche.ressourceHeader = this.ressource;
        if (route) {
          this.notifRoute = route;
          if (route && route.attribut) {
            this.attribut = route.attribut;
          } else {
            this.attribut = undefined;
          }
          if (route.section && route.section != Sections.NONE) {
            this.lastShowSection = !this.lastShowSection;
            this.selectedFiche.showSection = this.lastShowSection;
          }
        }
        const index = this.lastSelectedFiche.findIndex(item => {
          return item.component === this.selectedFiche.component;
        })
        if (index >= 0) {
          this.lastRoutes.splice(index + 1)
          this.lastSelectedFiche.splice(index + 1)
        } else {
          this.lastRoutes.push(route);
          this.lastSelectedFiche.push(this.selectedFiche);
        }
      }
    }
  })
</script>
